/**
 * Pure helpers for the Contacts page: handle normalisation/display, list
 * filtering, form scaffolding and message formatting. Dependency-free and
 * unit-tested; the page itself only wires state.
 */

import type {
  Contact,
  ContactHandle,
  ContactInput,
  ContactKind,
  HandleKind,
  MessageAttachment,
} from '../api.js';

export type ContactFilter = 'all' | ContactKind;

export const KIND_LABEL: Record<ContactKind, string> = {
  client: 'Client',
  person: 'Person',
  vendor: 'Vendor',
};

export const FILTERS: { key: ContactFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'client', label: 'Clients' },
  { key: 'person', label: 'People' },
  { key: 'vendor', label: 'Vendors' },
];

/** Anything with an `@` is an email; everything else is treated as a phone. */
export function guessHandleKind(raw: string): HandleKind {
  return raw.includes('@') ? 'email' : 'phone';
}

/**
 * Canonical storage form so the dispatcher's lookups match regardless of how
 * a number was typed. Phones become E.164: a bare 10-digit US number gains
 * `+1`, an 11-digit one starting with 1 gains `+`, anything typed with a
 * leading `+` keeps its country code. Other digit strings pass through as
 * digits only. Emails are trimmed and lower-cased.
 */
export function normalizeHandle(kind: HandleKind, raw: string): string {
  const trimmed = raw.trim();
  if (kind === 'email') return trimmed.toLowerCase();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits;
}

/** `+15405551234` → `(540) 555-1234`; anything else renders as stored. */
export function displayHandle(value: string, kind: HandleKind = guessHandleKind(value)): string {
  if (kind === 'email') return value;
  const m = value.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (!m) return value;
  return `(${m[1]}) ${m[2]}-${m[3]}`;
}

/** The handle a row leads with: first phone, else first email, else none. */
export function primaryHandle(c: Pick<Contact, 'handles'>): ContactHandle | null {
  return c.handles.find((h) => h.kind === 'phone') ?? c.handles[0] ?? null;
}

export function emptyContact(kind: ContactKind = 'client'): ContactInput {
  return {
    kind,
    name: '',
    contactName: null,
    project: null,
    domain: null,
    staging: null,
    docker: null,
    notes: null,
    auto: false,
    handles: [],
  };
}

/** Prefill for "Add as contact" from an inbox message. */
export function contactFromHandle(handle: string, displayName?: string | null): ContactInput {
  const kind = guessHandleKind(handle);
  return {
    ...emptyContact('client'),
    name: displayName?.trim() ?? '',
    handles: [{ kind, value: normalizeHandle(kind, handle) }],
  };
}

/** Strip the server-owned fields so a stored contact can seed the form. */
export function toContactInput(c: Contact): ContactInput {
  const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = c;
  return { ...rest, handles: rest.handles.map((h) => ({ ...h })) };
}

function nullIfBlank(v: string | null | undefined): string | null {
  const t = v?.trim() ?? '';
  return t === '' ? null : t;
}

/**
 * What actually gets sent: trimmed strings (blank → null), normalised handles
 * with empty rows dropped, and site fields cleared for anything that is not a
 * client so a kind change never leaves stale deploy details behind.
 */
export function cleanContactInput(input: ContactInput): ContactInput {
  const isClient = input.kind === 'client';
  const handles = input.handles
    .map((h) => ({
      ...(h.id !== undefined ? { id: h.id } : {}),
      kind: h.kind,
      value: normalizeHandle(h.kind, h.value),
      label: nullIfBlank(h.label),
    }))
    .filter((h) => h.value !== '');
  return {
    kind: input.kind,
    name: input.name.trim(),
    contactName: nullIfBlank(input.contactName),
    project: isClient ? nullIfBlank(input.project) : null,
    domain: isClient ? nullIfBlank(input.domain) : null,
    staging: isClient ? nullIfBlank(input.staging) : null,
    docker: isClient ? nullIfBlank(input.docker) : null,
    notes: nullIfBlank(input.notes),
    auto: isClient ? input.auto : false,
    handles,
  };
}

/** Case-insensitive match on name, contact name, project, domain and handles; sorted by name. */
export function filterContacts(contacts: Contact[], filter: ContactFilter, query: string): Contact[] {
  const q = query.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  return contacts
    .filter((c) => filter === 'all' || c.kind === filter)
    .filter((c) => {
      if (!q) return true;
      const hay = [c.name, c.contactName, c.project, c.domain].filter(Boolean).join(' ').toLowerCase();
      if (hay.includes(q)) return true;
      return c.handles.some(
        (h) => h.value.toLowerCase().includes(q) || (digits.length >= 3 && h.value.replace(/\D/g, '').includes(digits)),
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** "2:14 PM" today, "Sep 3 · 2:14 PM" this year, "Sep 3, 2025 · 2:14 PM" otherwise. */
export function fmtMessageTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return time;
  const sameYear = d.getFullYear() === now.getFullYear();
  const day = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${day} · ${time}`;
}

/** "image · IMG_0412.heic", "file · invoice.pdf", or just the type when nameless. */
export function attachmentLabel(a: MessageAttachment): string {
  const name = (a.local ?? a.path ?? '').split('/').pop() ?? '';
  const mime = a.mime ?? '';
  const type = mime.startsWith('image/')
    ? 'image'
    : mime.startsWith('video/')
      ? 'video'
      : mime.startsWith('audio/')
        ? 'audio'
        : 'file';
  return name ? `${type} · ${name}` : type;
}
